/**
 * OzelAcenteReservationForm — single-page rapid-entry reservation form for
 * B2C ("özel acente") sales.
 *
 * Distinct from the 5-step wizard (AcenteReservationForm.jsx) that group
 * agencies use — here the sales flow is one customer at a time, so the form
 * is deliberately compact:
 *
 *   1. Müşteri (telefon MANDATORY, ad opsiyonel)
 *   2. Otel & tarih
 *   3. Kişi & yemek
 *   4. Oda dağılımı
 *   5. Alınacak Ücret (renamed from "Anlaşılan Fiyat" for B2C context)
 *   6. Not
 *
 * All on ONE page — the operator sees everything at once and Ctrl+S / Kaydet
 * submits. No stepper, no "Devam" button, no wizard state.
 *
 * AI photo shortcut is retained (single input at the top) so a photo of a
 * handwritten note can still populate the whole form in one shot.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { numberFieldProps, dateFieldProps } from "@/lib/numberInput";
import { NumberInput } from "@/components/NumberInput";
import { DatePicker } from "@/components/DatePicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Save, ArrowLeft, BedDouble, Users, Calendar, Sparkles, Camera,
  Loader2, AlertTriangle, CheckCircle2, Phone, Wallet, Wand2, StickyNote,
} from "lucide-react";
import CapacityMismatchModal from "./CapacityMismatchModal";
import HotelCombobox from "./HotelCombobox";
import { computeReservationTotal } from "@/lib/pricing";

function _computedNights(ci, co) {
  if (!ci || !co) return 0;
  const a = new Date(ci + "T00:00:00");
  const b = new Date(co + "T00:00:00");
  const ms = b - a;
  return ms > 0 ? Math.round(ms / 86400000) : 0;
}

function emptyForm() {
  return {
    group_name: "",
    customer_phone: "",
    hotel_name: "",
    checkin: "",
    checkout: "",
    notes: "",
    rooms: { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 },
    adults: 1,
    children: 0,
    staff: 0,
    meal_start: "kahvalti",
    meal_end: "aksam",
    agreed_room_price: 0,
    agreed_meal_price: 0,
    agreed_price_currency: "SAR",
    room_unit_price: 0,   // SAR per oda per gece
    meal_unit_price: 0,   // SAR per kişi per gün (2 öğün)
  };
}

const MEAL_OPTIONS = [
  { v: "kahvalti", t: "Kahvaltı" },
  { v: "aksam", t: "Akşam" },
];

// Shared UX helpers moved to /app/frontend/src/lib/numberInput.js — see
// that file for the rationale. Importing keeps the fix in one place.
// import at top of file.

export default function OzelAcenteReservationForm() {
  const { user } = useAcenteAuth();
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const aiFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRoom = (k, v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, parseInt(v || 0, 10)) } }));
  const setNum = (k, v) => setForm((f) => ({ ...f, [k]: Math.max(k === "adults" ? 1 : 0, parseInt(v || 0, 10)) }));

  // Iter 39: Acente-otel kayıtlı fiyatı otomatik doldurma (yeni kayıt için).
  const [autofilledPrice, setAutofilledPrice] = useState(null);
  useEffect(() => {
    if (isEdit) return;
    const agencyId = user?.agency_id;
    const hotelName = (form.hotel_name || "").trim();
    if (!agencyId || !hotelName) {
      setAutofilledPrice(null);
      return;
    }
    let cancelled = false;
    panelApi
      .get(`/agency-price-lookup?agency_id=${encodeURIComponent(agencyId)}&hotel_name=${encodeURIComponent(hotelName)}`)
      .then((r) => {
        if (cancelled) return;
        const p = r.data || {};
        setForm((f) => {
          const next = { ...f };
          if ((!f.room_unit_price || f.room_unit_price === 0) && (p.room_unit_price || 0) > 0) {
            next.room_unit_price = p.room_unit_price;
          }
          if ((!f.meal_unit_price || f.meal_unit_price === 0) && (p.meal_unit_price || 0) > 0) {
            next.meal_unit_price = p.meal_unit_price;
          }
          return next;
        });
        setAutofilledPrice({ room: p.room_unit_price, meal: p.meal_unit_price });
      })
      .catch(() => setAutofilledPrice(null));
    return () => { cancelled = true; };
  }, [form.hotel_name, isEdit, user?.agency_id]);

  // Merge AI-extracted fields into the form, only overwriting empty values so
  // user edits are never trampled.
  const applyAiData = (d) => {
    setForm((f) => {
      const next = { ...f };
      if (d.group_name && !f.group_name) next.group_name = d.group_name;
      if (d.customer_phone && !f.customer_phone) next.customer_phone = d.customer_phone;
      if (d.hotel_name && !f.hotel_name) next.hotel_name = d.hotel_name;
      if (d.checkin && !f.checkin) next.checkin = d.checkin;
      if (d.checkout && !f.checkout) next.checkout = d.checkout;
      if (d.adults != null) next.adults = d.adults;
      if (d.children != null) next.children = d.children;
      if (d.staff != null) next.staff = d.staff;
      if (d.rooms) next.rooms = { r1: d.rooms.r1 || 0, r2: d.rooms.r2 || 0, r3: d.rooms.r3 || 0, r4: d.rooms.r4 || 0, r5: d.rooms.r5 || 0 };
      if (d.meal_start) next.meal_start = d.meal_start;
      if (d.meal_end) next.meal_end = d.meal_end;
      if (d.notes && !f.notes) next.notes = d.notes;
      return next;
    });
  };

  const handleAiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ok = ["image/jpeg", "image/png", "image/webp"];
    if (files.some((f) => !ok.includes(f.type))) {
      toast.error("Sadece JPEG/PNG/WEBP dosyaları desteklenir");
      e.target.value = "";
      return;
    }
    if (files.some((f) => f.size > 8 * 1024 * 1024)) {
      toast.error("Her fotoğraf en fazla 8 MB olabilir");
      e.target.value = "";
      return;
    }
    if (files.length > 6) {
      toast.error("En fazla 6 fotoğraf yükleyebilirsiniz");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setAiBusy(true);
    try {
      const r = await panelApi.post("/ai/extract-reservation", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      applyAiData(r?.data?.data || {});
      toast.success("Bilgiler formu dolduruldu — kontrol edip kaydedin", { duration: 5000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bilgi çıkarılamadı. Daha net bir fotoğraf deneyin.");
    } finally {
      setAiBusy(false);
      e.target.value = "";
    }
  };

  // Live calculations so the operator sees the impact of their entries.
  const nights = useMemo(() => {
    if (!form.checkin || !form.checkout) return 0;
    const a = new Date(form.checkin);
    const b = new Date(form.checkout);
    return Math.max(0, Math.round((b - a) / 86400000));
  }, [form.checkin, form.checkout]);

  const totalRooms = useMemo(
    () => (form.rooms.r1 || 0) + (form.rooms.r2 || 0) + (form.rooms.r3 || 0) + (form.rooms.r4 || 0) + (form.rooms.r5 || 0),
    [form.rooms]
  );
  const roomCapacity = useMemo(
    () =>
      1 * (form.rooms.r1 || 0) +
      2 * (form.rooms.r2 || 0) +
      3 * (form.rooms.r3 || 0) +
      4 * (form.rooms.r4 || 0) +
      5 * (form.rooms.r5 || 0),
    [form.rooms]
  );
  const totalPeople = useMemo(
    () => (form.adults || 0) + (form.children || 0) + (form.staff || 0),
    [form.adults, form.children, form.staff]
  );
  const capacityDiff = roomCapacity - totalPeople;
  const showCapacityWarn = totalRooms > 0 && totalPeople > 0 && capacityDiff !== 0;
  const capacityOk = totalRooms > 0 && totalPeople > 0 && capacityDiff === 0;

  // Load reservation on edit.
  useEffect(() => {
    if (!isEdit) return;
    panelApi.get(`/reservations/${id}`).then((r) => {
      const d = r.data;
      setForm({
        group_name: d.group_name || "",
        customer_phone: d.customer_phone || "",
        hotel_name: d.hotel_name || "",
        checkin: d.checkin || "",
        checkout: d.checkout || "",
        notes: d.notes || "",
        rooms: { r1: d.rooms?.r1 || 0, r2: d.rooms?.r2 || 0, r3: d.rooms?.r3 || 0, r4: d.rooms?.r4 || 0, r5: d.rooms?.r5 || 0 },
        adults: d.adults ?? 1,
        children: d.children ?? 0,
        staff: d.staff ?? 0,
        meal_start: d.meal_start || "kahvalti",
        meal_end: d.meal_end || "aksam",
        agreed_room_price: d.agreed_room_price ?? 0,
        agreed_meal_price: d.agreed_meal_price ?? 0,
        agreed_price_currency: d.agreed_price_currency || "SAR",
        room_unit_price: d.room_unit_price ?? 0,
        meal_unit_price: d.meal_unit_price ?? 0,
      });
    }).catch(() => toast.error("Rezervasyon yüklenemedi"));
  }, [id, isEdit]);

  const bumpCheckout = (n) => {
    if (!form.checkin) return toast.error("Önce giriş tarihini seçin");
    const d = new Date(form.checkin);
    d.setDate(d.getDate() + n);
    setField("checkout", d.toISOString().slice(0, 10));
  };

  const suggestRooms = () => {
    let remaining = totalPeople;
    if (remaining <= 0) return;
    const rooms = { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 };
    for (const [k, size] of [["r3", 3], ["r2", 2], ["r4", 4], ["r5", 5], ["r1", 1]]) {
      if (remaining <= 0) break;
      const n = Math.floor(remaining / size);
      if (n > 0) { rooms[k] = n; remaining -= n * size; }
    }
    if (remaining > 0) rooms.r1 += remaining;
    setForm((f) => ({ ...f, rooms }));
    toast.success("Oda dağılımı önerildi — kontrol edin");
  };

  const [capacityModalOpen, setCapacityModalOpen] = useState(false);

  const doPersist = async () => {
    setCapacityModalOpen(false);
    setBusy(true);
    try {
      // Iter 26: compute grand totals from unit prices; backend accepts
      // both the unit numbers (round-trip) and the computed totals.
      const { roomTotal, mealTotal } = computeReservationTotal({
        rooms: form.rooms,
        nights: _computedNights(form.checkin, form.checkout),
        adults: form.adults,
        children: form.children,
        staff: form.staff,
        meal_start: form.meal_start,
        meal_end: form.meal_end,
        room_unit_price: form.room_unit_price,
        meal_unit_price: form.meal_unit_price,
      });
      const payload = {
        ...form,
        agreed_room_price: roomTotal,
        agreed_meal_price: mealTotal,
      };
      // Özel acente never sends agency_id — backend uses their own.
      delete payload.agency_id;
      if (isEdit) {
        await panelApi.put(`/reservations/${id}`, payload);
        toast.success("Rezervasyon güncellendi");
      } else {
        await panelApi.post("/reservations", payload);
        toast.success("Rezervasyon oluşturuldu");
      }
      nav("/acente/rezervasyonlar");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : typeof detail === "string" ? detail : "Kayıt yapılamadı";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Hard validation: phone mandatory for özel acente.
    if (!form.customer_phone.trim()) {
      toast.error("Müşteri telefon numarası zorunludur");
      return;
    }
    if (!form.hotel_name.trim()) {
      toast.error("Otel adı zorunlu");
      return;
    }
    if (!form.checkin || !form.checkout) {
      toast.error("Giriş ve çıkış tarihi zorunlu");
      return;
    }
    if (form.checkout < form.checkin) {
      toast.error("Çıkış tarihi giriş tarihinden önce olamaz");
      return;
    }
    if (form.adults < 1) {
      toast.error("Yetişkin sayısı en az 1 olmalı");
      return;
    }
    if (totalRooms === 0) {
      toast.error("En az bir oda tipi için sayı girilmiş olmalı");
      return;
    }
    if (showCapacityWarn) {
      setCapacityModalOpen(true);
      return;
    }
    await doPersist();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="ozel-acente-reservation-form-page">
      <div className="text-center sm:text-left">
        <button onClick={() => nav(-1)} className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1" data-testid="ozel-form-back">
          <ArrowLeft size={14} /> Geri
        </button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-700 font-semibold mt-2">Özel Acente — Bireysel Satış</div>
        <h1 className="mt-1 font-serif text-3xl text-brand-navy">
          {isEdit ? "Rezervasyonu Düzenle" : "Hızlı Rezervasyon Oluştur"}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Tek sayfada tüm bilgileri girin. Müşteri telefonu zorunludur — diğer alanları elinizdeki fotoğraftan otomatik doldurabilirsiniz.
        </p>
      </div>

      {/* Live calculation strip */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <LiveTile icon={<Calendar size={16}/>} label="Gece" value={nights} accent="brand-navy" testid="ozel-live-nights"/>
        <LiveTile icon={<BedDouble size={16}/>} label="Oda" value={totalRooms} accent="brand-orange" testid="ozel-live-rooms"/>
        <LiveTile icon={<Users size={16}/>} label="Kapasite" value={roomCapacity} accent="brand-navy" testid="ozel-live-room-capacity"/>
        <LiveTile icon={<Users size={16}/>} label="Kişi" value={totalPeople} accent="brand-gold" testid="ozel-live-total-people"/>
      </div>

      {/* AI photo shortcut */}
      {!isEdit && (
        <div
          className="rounded-2xl border border-brand-orange/30 bg-gradient-to-br from-brand-orange/10 via-white to-brand-gold/10 p-4 sm:p-5 shadow-sm"
          data-testid="ozel-ai-photo-upload-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-orange to-brand-orange-dark text-white flex items-center justify-center shadow-md">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Yapay Zeka Yardımcısı</div>
              <div className="font-serif text-lg text-brand-navy mt-0.5">Fotoğraftan bilgileri otomatik doldur</div>
              <p className="text-xs text-slate-600 mt-1">
                WhatsApp yazışması, elle yazılmış not veya rezervasyon ekran görüntüsünü yükleyin — sistem müşteri adı, telefon, otel, tarihler, oda ve kişi sayılarını otomatik doldurur.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={aiFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={handleAiUpload}
                data-testid="ozel-ai-photo-input"
              />
              <Button
                type="button"
                onClick={() => aiFileRef.current?.click()}
                disabled={aiBusy}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-70"
                data-testid="ozel-ai-photo-upload-btn"
              >
                {aiBusy ? <><Loader2 size={14} className="mr-2 animate-spin" /> AI analiz ediyor…</> : <><Camera size={14} className="mr-2" /> Fotoğraf Yükle</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={submit}
        className="bg-white rounded-2xl border border-brand-navy/10 p-6 sm:p-8 grid gap-8 shadow-sm"
        data-testid="ozel-acente-reservation-form"
      >
        {/* SECTION 1 — Müşteri */}
        <FormSection
          title="Müşteri Bilgileri"
          hint="Telefon numarası zorunludur. Ad opsiyoneldir — istemezseniz sadece telefonla kayıt yapabilirsiniz."
          icon={<Phone size={16}/>}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Müşteri Telefonu <span className="text-rose-600">*</span></Label>
              <Input
                type="tel"
                inputMode="tel"
                value={form.customer_phone}
                onChange={(e) => setField("customer_phone", e.target.value)}
                placeholder="+90 5XX XXX XX XX"
                required
                data-testid="ozel-customer-phone"
                autoFocus
              />
              <p className="text-[11px] text-slate-500 mt-1">Zorunlu alan — müşteriye ulaşmak için tek anchor.</p>
            </div>
            <div>
              <Label>Müşteri / Grup Adı <span className="text-slate-400">(opsiyonel)</span></Label>
              <Input
                value={form.group_name}
                onChange={(e) => setField("group_name", e.target.value)}
                placeholder="Örn. Ali Yılmaz"
                data-testid="ozel-group-name"
              />
            </div>
          </div>
        </FormSection>

        {/* SECTION 2 — Otel & Tarih */}
        <FormSection
          title="Otel ve Tarihler"
          hint="Otel adını listeden seçin veya yeni bir otel yazın. Tarih için hızlı '+N gece' butonlarını kullanabilirsiniz."
          icon={<Calendar size={16}/>}
        >
          <div className="grid gap-4">
            <div>
              <Label>Otel Adı <span className="text-rose-600">*</span></Label>
              <HotelCombobox
                value={form.hotel_name}
                onChange={(v) => setField("hotel_name", v)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Giriş Tarihi <span className="text-rose-600">*</span></Label>
                <DatePicker value={form.checkin} onChange={(v) => setField("checkin", v)} required placeholder="Giriş tarihi seçin" data-testid="ozel-checkin" />
              </div>
              <div>
                <Label>Çıkış Tarihi <span className="text-rose-600">*</span></Label>
                <DatePicker value={form.checkout} onChange={(v) => setField("checkout", v)} required minDate={form.checkin || undefined} placeholder="Çıkış tarihi seçin" data-testid="ozel-checkout" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1 self-center">Hızlı süre:</span>
              {[3, 5, 7, 10, 14].map((n) => (
                <Button
                  key={n}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => bumpCheckout(n)}
                  data-testid={`ozel-quick-${n}n`}
                  disabled={!form.checkin}
                  className="text-xs border-brand-orange/30 hover:bg-brand-orange/10 disabled:opacity-50"
                >
                  +{n} gece
                </Button>
              ))}
              {nights > 0 && (
                <span className="ml-auto text-xs text-brand-navy self-center">
                  Toplam <b>{nights}</b> gece
                </span>
              )}
            </div>
          </div>
        </FormSection>

        {/* SECTION 3 — Kişi & Yemek */}
        <FormSection
          title="Kişi Sayısı ve Yemek Planı"
          hint="Toplam kişi ve hangi öğünlerle başlayıp bitirilecek."
          icon={<Users size={16}/>}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Yetişkin <span className="text-rose-600">*</span></Label>
              <NumberInput min={1} value={form.adults} onChange={(v) => setForm((f) => ({ ...f, adults: Math.max(1, v || 0) }))} required data-testid="ozel-adults" />
            </div>
            <div>
              <Label>Çocuk</Label>
              <NumberInput min={0} value={form.children} onChange={(v) => setForm((f) => ({ ...f, children: Math.max(0, v || 0) }))} data-testid="ozel-children" />
            </div>
            <div>
              <Label>Görevli</Label>
              <NumberInput min={0} value={form.staff} onChange={(v) => setForm((f) => ({ ...f, staff: Math.max(0, v || 0) }))} data-testid="ozel-staff" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Başlangıç Öğünü</Label>
              <select value={form.meal_start} onChange={(e) => setField("meal_start", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="ozel-meal-start">
                {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
              </select>
            </div>
            <div>
              <Label>Bitiş Öğünü</Label>
              <select value={form.meal_end} onChange={(e) => setField("meal_end", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="ozel-meal-end">
                {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
              </select>
            </div>
          </div>
        </FormSection>

        {/* SECTION 4 — Oda Dağılımı */}
        <FormSection
          title="Oda Dağılımı"
          hint="Kaç kişilik odaları kaçar tane istiyorsunuz? Otomatik öneri de kullanabilirsiniz."
          icon={<BedDouble size={16}/>}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-600">
              Toplam kişi: <b className="text-brand-navy">{totalPeople}</b> · Oda kapasitesi: <b className="text-brand-navy">{roomCapacity}</b>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={suggestRooms}
              disabled={totalPeople <= 0}
              className="text-xs border-brand-orange/30 hover:bg-brand-orange/10 disabled:opacity-50"
              data-testid="ozel-suggest-rooms"
            >
              <Wand2 size={13} className="mr-1" /> Otomatik oda dağılımı öner
            </Button>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
            {["r1", "r2", "r3", "r4", "r5"].map((k) => {
              const labels = { r1: "1 Kişilik", r2: "2 Kişilik", r3: "3 Kişilik", r4: "4 Kişilik", r5: "5 Kişilik" };
              return (
                <div key={k}>
                  <Label>{labels[k]}</Label>
                  <NumberInput min={0} value={form.rooms[k]} onChange={(v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, v || 0) } }))} data-testid={`ozel-room-${k}`} />
                </div>
              );
            })}
          </div>

          {showCapacityWarn && (
            <div
              className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${capacityDiff > 0 ? "border-amber-400 bg-amber-50" : "border-rose-400 bg-rose-50"}`}
              data-testid="ozel-capacity-warning-inline"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${capacityDiff > 0 ? "bg-amber-500 text-white" : "bg-rose-600 text-white"}`}>
                <AlertTriangle size={18} />
              </div>
              <div className="text-sm">
                <div className={`font-semibold ${capacityDiff > 0 ? "text-amber-900" : "text-rose-900"}`}>
                  Oda kapasitesi ve kişi sayısı uyuşmuyor
                </div>
                <div className={`text-xs mt-1 ${capacityDiff > 0 ? "text-amber-800" : "text-rose-800"}`}>
                  Kapasite <b>{roomCapacity}</b> · Kişi <b>{totalPeople}</b>
                  {capacityDiff > 0 ? <> — <b>{capacityDiff}</b> kişilik fazla yer var.</> : <> — <b>{Math.abs(capacityDiff)}</b> kişi yetersiz.</>}
                </div>
              </div>
            </div>
          )}
          {capacityOk && (
            <div
              className="rounded-2xl border border-emerald-300 bg-emerald-50 text-emerald-900 p-3 flex items-center gap-3 text-sm"
              data-testid="ozel-capacity-ok-inline"
            >
              <div className="w-8 h-8 rounded-xl bg-emerald-200 text-emerald-800 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={15} />
              </div>
              <div>
                <b>Oda ve kişi sayınız uyumlu.</b> {totalPeople} kişi için oda kapasiteniz tam {roomCapacity}.
              </div>
            </div>
          )}
        </FormSection>

        {/* SECTION 5 — Alınacak Ücret */}
        <FormSection
          title="Alınacak Ücret"
          hint="Müşteriden alacağınız ücreti girin. Bu bilgi operasyon ekibine iletilir."
          icon={<Wallet size={16}/>}
        >
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Wallet size={12} />
              </div>
              <div className="text-sm text-emerald-900">
                <p className="font-medium">Müşterinizden alacağınız oda ve yemek ücretlerini girin.</p>
                <p className="text-xs text-emerald-800/80 mt-1">
                  <b>Not:</b> Girdiğiniz tutarlar operasyon ekibimiz tarafından doğrulanacaktır. Onay sonrası bu değerler rezervasyona sabitlenir.
                </p>
              </div>
            </div>

            {/* Yüksek görünürlüklü uyarı — özel acente'nin girdiği tutar
                doğrudan müşteriden tahsil edileceği için, dikkatsizce
                girilmesi ciddi maddi zarara neden olabilir. */}
            <div
              className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3"
              data-testid="ozel-fee-collection-warning"
            >
              <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={14} />
              </div>
              <div className="text-sm text-amber-900">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Önemli Uyarı</div>
                <p className="mt-0.5 leading-snug">
                  Belirttiğiniz tutar ekibimiz tarafından müşteriden tahsil edilecektir,
                  lütfen doğru bilgileri girmeye özen gösteriniz.
                </p>
              </div>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Oda Fiyatı <span className="text-[11px] text-slate-500">— 1 oda / 1 gece · SAR ﷼</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.room_unit_price}
                  onChange={(e) => setField("room_unit_price", Math.max(0, parseFloat(e.target.value || 0)))}
                  data-testid="ozel-fee-room-price"
                  {...numberFieldProps}
                />
              </div>
              <div>
                <Label>Yemek Fiyatı <span className="text-[11px] text-slate-500">— 1 kişi / 1 gün (2 öğün) · SAR ﷼</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.meal_unit_price}
                  onChange={(e) => setField("meal_unit_price", Math.max(0, parseFloat(e.target.value || 0)))}
                  data-testid="ozel-fee-meal-price"
                  {...numberFieldProps}
                />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-emerald-800/80">
              Yemek toplamı yalnızca <b>yetişkin sayısı</b> üzerinden hesaplanır (çocuk ve görevli hariç).
            </p>
            {(() => {
              const nights = _computedNights(form.checkin, form.checkout);
              const t = computeReservationTotal({
                rooms: form.rooms,
                nights,
                adults: form.adults,
                children: form.children,
                staff: form.staff,
                meal_start: form.meal_start,
                meal_end: form.meal_end,
                room_unit_price: form.room_unit_price,
                meal_unit_price: form.meal_unit_price,
              });
              if (t.roomTotal <= 0 && t.mealTotal <= 0) return null;
              return (
                <div className="mt-3 space-y-2" data-testid="ozel-price-breakdown">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="rounded-xl bg-white border border-emerald-300 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Oda Toplamı</div>
                      <div className="text-xs text-slate-600 mt-0.5">{t.totalRooms} oda × {nights} gece × {Number(form.room_unit_price || 0).toLocaleString("tr-TR")} ﷼</div>
                      <div className="font-serif text-xl text-emerald-800 mt-1" data-testid="ozel-room-total">
                        {t.roomTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                      </div>
                    </div>
                    <div className="rounded-xl bg-white border border-emerald-300 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Yemek Toplamı</div>
                      <div className="text-xs text-slate-600 mt-0.5">{t.totalMeals} öğün × {t.mealPersons} yetişkin × {t.perMealPrice.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼</div>
                      <div className="font-serif text-xl text-emerald-800 mt-1" data-testid="ozel-meal-total">
                        {t.mealTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-3 shadow-md">
                    <div className="text-xs uppercase tracking-widest font-semibold">Toplam Alınacak Ücret</div>
                    <div className="font-serif text-2xl" data-testid="ozel-fee-total">
                      {t.grandTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
                      <span className="text-sm opacity-90">SAR ﷼</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </FormSection>

        {/* SECTION 6 — Not */}
        <FormSection
          title="Özel Notlar"
          hint="Varsa özel istekleri buraya yazabilirsiniz (opsiyonel)."
          icon={<StickyNote size={16}/>}
        >
          <Textarea
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            rows={3}
            placeholder="Örn. Geç check-in, uçuş bilgisi, alerji notu..."
            data-testid="ozel-notes"
          />
        </FormSection>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={() => nav("/acente/rezervasyonlar")} data-testid="ozel-cancel">
            Vazgeç
          </Button>
          <Button type="submit" disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="ozel-submit">
            <Save size={16} className="mr-1" /> {busy ? "Kaydediliyor..." : (isEdit ? "Güncelle" : "Kaydet")}
          </Button>
        </div>
      </form>

      <CapacityMismatchModal
        open={capacityModalOpen}
        onCancel={() => setCapacityModalOpen(false)}
        onConfirm={doPersist}
        roomCapacity={roomCapacity}
        totalPeople={totalPeople}
        adults={form.adults}
        children={form.children}
        staff={form.staff}
        rooms={form.rooms}
      />
    </div>
  );
}

function FormSection({ title, hint, icon, children }) {
  return (
    <section className="grid gap-4">
      <div className="flex items-center gap-2">
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
        )}
        <div>
          <h2 className="font-serif text-xl text-brand-navy leading-tight">{title}</h2>
          {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function LiveTile({ icon, label, value, accent, testid }) {
  const bgMap = {
    "brand-navy": "from-brand-navy/10 to-brand-navy/5 text-brand-navy",
    "brand-orange": "from-brand-orange/10 to-brand-orange/5 text-brand-orange-dark",
    "brand-gold": "from-brand-gold/20 to-brand-gold/5 text-brand-navy",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${bgMap[accent]} border border-white p-4 flex items-center gap-3`} data-testid={testid}>
      <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        <div className="font-serif text-2xl leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}
